<template>
    <div class="email-detail-list">
        <!-- 头部工具栏 -->
        <div class="email-detail-toolbar">
            <div class="toolbar-left">
                <el-button size="small" @click="goBack">
                    <i class="el-icon-arrow-left"></i>
                    返回
                </el-button>
                <div class="user-info">
                    <img class="user-avatar" :src="userInfo?.avatar_small || require('@/assets/profile.png')" alt="">
                    <div class="user-details">
                        <div class="user-name">{{ userInfo?.name }}</div>
                        <div class="user-status" :class="userInfo?.is_online ? 'online' : 'offline'">
                            {{ userInfo?.is_online ? '在线' : '离线' }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="toolbar-right">
                <el-button type="primary" size="small" @click="replyEmail">
                    <i class="el-icon-edit"></i>
                    回复
                </el-button>
            </div>
        </div>

        <!-- 邮件列表 -->
        <div class="email-list-container" ref="emailListContainer">
            <!-- 加载状态 -->
            <div v-if="loading && emailList.length === 0" class="loading-container">
                <i class="el-icon-loading fs-24 color-black"></i>
                <div class="fs-14 color-gray ml-10">正在加载邮件...</div>
            </div>

            <!-- 空状态 -->
            <div v-else-if="!loading && emailList.length === 0" class="empty-container">
                <div class="empty-icon">
                    <img src="@/assets/logo/128x128.png" alt="">
                </div>
                <div class="empty-title">暂无邮件记录</div>
                <div class="empty-subtitle">开始发送第一封邮件吧</div>
            </div>

            <!-- 邮件列表 -->
            <div v-else class="email-list">
                <div v-for="(email, index) in emailList" :key="`email-${index}-${email.id}`" class="email-item"
                    :class="{ 'expanded': email.isExpanded }">
                    <!-- 邮件头部（折叠状态显示） -->
                    <div class="email-header" @click="toggleEmailExpansion(email)">
                        <!-- 左侧：发送者信息 -->
                        <div class="email-sender-info">
                            <div class="sender-avatar-container">
                                <img class="sender-avatar" :src="getSenderAvatar(email)" alt="">
                                <div class="online-indicator" :class="isCurrentUser(email) ? 'online' : 'offline'">
                                </div>
                            </div>
                            <div class="sender-name">{{ getSenderName(email) }}</div>
                        </div>

                        <!-- 中间：邮件内容预览 -->
                        <div class="email-preview">
                            <div class="email-content-preview" :class="{ 'unread': email.status !== 'read' }">
                                {{ stripHtmlTags(email.content) }}
                            </div>
                        </div>

                        <!-- 右侧：邮件元数据和操作 -->
                        <div class="email-meta-actions">
                            <!-- 附件图标 -->
                            <div v-if="email.attachments && Object.keys(email.attachments).length > 0"
                                class="attachment-indicator">
                                <img src="@/assets/svg/link.svg" alt="附件">
                            </div>

                            <!-- 状态标签 -->
                            <div class="status-tag" :class="email.status === 'read' ? 'read' : 'unread'">
                                <img v-if="email.status === 'read'" src="@/assets/svg/read.svg" alt="已读">
                                <img v-else src="@/assets/svg/unread.svg" alt="未读">
                                <span>{{ email.status === 'read' ? '已读' : '未读' }}</span>
                            </div>

                            <!-- 时间 -->
                            <div class="email-time">{{ formatDate(email.date_created) }}</div>

                            <!-- 收藏图标 -->
                            <div class="star-icon" @click.stop="toggleStar(email)">
                                <img :src="email.is_starred ? require('@/assets/svg/star-active.svg') : require('@/assets/svg/star.svg')"
                                    alt="收藏">
                            </div>
                        </div>
                    </div>

                    <!-- 邮件内容（展开时显示） -->
                    <div class="email-content" v-show="email.isExpanded">
                        <div class="email-body" :class="{ 'unread': email.status !== 'read' }" v-html="email.content">
                        </div>

                        <!-- 附件 -->
                        <div v-if="hasAttachments(email)" class="email-attachments">
                            <div class="attachment-header">
                                <img src="@/assets/svg/link.svg" alt="附件">
                                <span>附件</span>
                            </div>
                            <div class="attachment-list">
                                <!-- 图片附件 -->
                                <div v-if="email.attachments?.images && email.attachments.images.length > 0"
                                    class="attachment-section">
                                    <div class="section-title">图片</div>
                                    <div class="attachment-items">
                                        <div v-for="(image, imgIndex) in email.attachments.images" :key="`email-${email.id}-image-${imgIndex}-${image.id}`"
                                            class="attachment-item"
                                            @click="handleAttachmentClick(email, image, 'image', imgIndex)">
                                            <div class="attachment-preview-container">
                                                <imagePreview :src="image.url_thumbnail || image.url_original"
                                                    :previewSrcList="email.attachments.images" :currentIndex="imgIndex"
                                                    class="post-img" :listFields="'url_original'" />
                                                <div v-if="image.is_paid" class="seen-overlay">
                                                    <span class="seen-tag">
                                                        <img src="@/assets/svg/read.svg" alt="已查看">
                                                        seen
                                                    </span>
                                                </div>
                                                <div v-else class="seen-overlay">
                                                    <span class="seen-tag seen-red">
                                                        <img src="@/assets/svg/unread.svg" alt="未查看">
                                                        unseen
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- 视频附件 -->
                                <div v-if="email.attachments?.videos && email.attachments.videos.length > 0"
                                    class="attachment-section">
                                    <div class="section-title">视频</div>
                                    <div class="attachment-items">
                                        <div v-for="(video, vidIndex) in email.attachments.videos"
                                            :key="`email-${email.id}-video-${vidIndex}-${video.id}`" class="attachment-item"
                                            @click="handleAttachmentClick(email, video, 'video', vidIndex)">
                                            <div class="attachment-preview-container">
                                                <div class="video-preview">
                                                    <img :src="video.url_thumbnail" class="attachment-preview" alt=""
                                                        @click="handleVideoClick(video)" />
                                                    <div class="video-play-icon">
                                                        <!-- <img src="@/assets/attachment/mttp1.png" alt="播放"> -->
                                                        <i class="el-icon-video-play" style="color: #fff;"></i>
                                                    </div>
                                                </div>
                                                <div v-if="video.is_paid" class="seen-overlay">
                                                    <span class="seen-tag">
                                                        <img src="@/assets/svg/read.svg" alt="已查看">
                                                        seen
                                                    </span>
                                                </div>
                                                <div v-else class="seen-overlay">
                                                    <span class="seen-tag seen-red">
                                                        <img src="@/assets/svg/unread.svg" alt="未查看">
                                                        unseen
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 邮件操作 -->
                        <!-- <div class="email-actions">
                            <div class="action-buttons">
                                <el-button size="mini" type="text" @click="toggleStar(email)">
                                    <i v-if="email.isToggleStar" class="el-icon-loading"></i>
                                    <img v-else
                                        :src="email.is_starred ? require('@/assets/svg/star-active.svg') : require('@/assets/svg/star.svg')"
                                        class="action-icon" alt="收藏">
                                </el-button>
                                <el-button size="mini" type="text" @click="deleteEmail(email)">
                                    <img src="@/assets/svg/delete.svg" class="action-icon" alt="删除">
                                </el-button>
                            </div>
                        </div> -->
                    </div>
                </div>

                <!-- 加载更多 -->
                <div v-if="loading && emailList.length > 0" class="load-more">
                    <i class="el-icon-loading fs-24 color-black"></i>
                    <div class="fs-14 color-gray ml-10">加载中...</div>
                </div>

                <!-- 没有更多数据 -->
                <div v-if="!hasMore && emailList.length > 0 && !loading" class="no-more">
                    <div class="fs-14 color-gray">已加载全部数据</div>
                </div>
            </div>
        </div>

        <!-- 回复邮件弹窗 -->
        <ReplyEmailModal :visible="showReplyModal" :userInfo="userInfo" :currentUserInfo="currentUserInfo"
            :recipient-name="userInfo?.name || '对方'" :recipient-id="correspondenceInfo?.idRegularUser || 0"
            @close="showReplyModal = false" />
    </div>
</template>

<script>
import { getCorrespondenceEmailsHistory, changeEmailStar } from '@/api/talkytimes/message';
import ImagePreview from '@/components/imagePreview.vue';
import ReplyEmailModal from './replyEmailModal.vue';

export default {
    name: 'EmailDetailList',
    components: {
        ImagePreview,
        ReplyEmailModal
    },
    props: {
        // 邮件对话信息
        correspondenceInfo: {
            type: Object,
            required: true
        },
        // 用户信息
        userInfo: {
            type: Object,
            default: () => ({})
        },
        // 当前用户信息（己方）
        currentUserInfo: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            emailList: [], // 邮件列表
            loading: false, // 加载状态
            hasMore: true, // 是否还有更多数据
            page: 1, // 当前页码
            limit: 10, // 每页数量
            scrollContainer: null, // 滚动容器引用
            showReplyModal: false, // 显示回复邮件弹窗
        }
    },
    mounted() {
        this.initScrollListener();
        this.loadEmails();
    },
    beforeDestroy() {
        this.removeScrollListener();
    },
    methods: {
        // 初始化滚动监听
        initScrollListener() {
            this.scrollContainer = this.$refs.emailListContainer;
            if (this.scrollContainer) {
                this.scrollContainer.addEventListener('scroll', this.handleScroll);
            }
        },

        // 移除滚动监听
        removeScrollListener() {
            if (this.scrollContainer) {
                this.scrollContainer.removeEventListener('scroll', this.handleScroll);
            }
        },

        // 处理滚动事件
        handleScroll() {
            if (this.loading || !this.hasMore) return;

            const { scrollTop, scrollHeight, clientHeight } = this.scrollContainer;
            const threshold = 50; // 距离底部50px时触发加载

            if (scrollHeight - scrollTop <= clientHeight + threshold) {
                this.loadMoreEmails();
            }
        },

        // 加载邮件列表
        async loadEmails(isLoadMore = false) {
            if (this.loading) return;

            this.loading = true;

            try {
                const params = {
                    id_correspondence: this.correspondenceInfo.id,
                    id_interlocutor: this.correspondenceInfo.idRegularUser,
                    id_user: this.correspondenceInfo.idTrustedUser,
                    limit: this.limit,
                    page: isLoadMore ? this.page + 1 : 1
                };

                const res = await getCorrespondenceEmailsHistory(params);

                if (res && res.data && res.data.history) {
                    const newEmails = res.data.history.map((email, index) => ({
                        ...email,
                        isExpanded: !isLoadMore && index === 0 // 首次加载时第一条数据默认展开
                    }));

                    if (isLoadMore) {
                        this.emailList = [...this.emailList, ...newEmails];
                        this.page++;
                    } else {
                        this.emailList = newEmails;
                        this.page = 1;
                    }

                    // 判断是否还有更多数据
                    this.hasMore = newEmails.length >= this.limit;
                } else {
                    this.hasMore = false;
                }
            } catch (error) {
                console.error('加载邮件列表失败:', error);
                this.$message.error('加载邮件列表失败');
            } finally {
                this.loading = false;
            }
        },

        // 切换邮件展开状态
        toggleEmailExpansion(email) {
            // 如果当前邮件已展开，则收起
            if (email.isExpanded) {
                this.$set(email, 'isExpanded', false);
            } else {
                // 如果当前邮件未展开，则展开当前邮件，同时收起其他邮件
                this.emailList.forEach(item => {
                    // 确保ID比较时类型一致
                    this.$set(item, 'isExpanded', String(item.id) === String(email.id));
                });
            }
        },

        // 加载更多邮件
        loadMoreEmails() {
            if (this.loading || !this.hasMore) return;
            this.loadEmails(true);
        },

        // 获取发送者头像
        getSenderAvatar(email) {
            // 根据数据，id_user_from 是发送者ID
            // 如果发送者是当前用户（idTrustedUser），显示当前用户头像
            if (String(email.id_user_from) === String(this.correspondenceInfo.idTrustedUser)) {
                return this.currentUserInfo?.avatar_small || require('@/assets/profile.png');
            } else {
                // 如果发送者是对方（idRegularUser），显示对方头像
                return this.userInfo?.avatar_small || require('@/assets/profile.png');
            }
        },

        // 获取发送者姓名
        getSenderName(email) {
            // 根据数据，id_user_from 是发送者ID
            // 如果发送者是当前用户（idTrustedUser），显示"我"
            if (String(email.id_user_from) === String(this.correspondenceInfo.idTrustedUser)) {
                return this.currentUserInfo?.name || '我';
            } else {
                // 如果发送者是对方（idRegularUser），显示对方姓名
                return this.userInfo?.name || '对方';
            }
        },

        // 判断是否为当前用户发送的消息
        isCurrentUser(email) {
            return String(email.id_user_from) === String(this.correspondenceInfo.idTrustedUser);
        },

        // 格式化日期
        formatDate(dateStr) {
            if (!dateStr) return '';

            let d;
            if (typeof dateStr === 'number' || /^\d+$/.test(dateStr)) {
                d = new Date(parseInt(dateStr) * 1000);
            } else {
                d = new Date(dateStr.replace(/-/g, '/').replace('T', ' ').replace(/\..*$/, ''));
            }

            if (isNaN(d.getTime())) return dateStr;

            const now = new Date();
            const currentYear = now.getFullYear();
            const dateYear = d.getFullYear();
            const month = (d.getMonth() + 1).toString().padStart(2, '0');
            const day = d.getDate().toString().padStart(2, '0');
            const hour = d.getHours().toString().padStart(2, '0');
            const minute = d.getMinutes().toString().padStart(2, '0');

            if (dateYear === currentYear) {
                return `${month}-${day} ${hour}:${minute}`;
            } else {
                return `${dateYear}-${month}-${day} ${hour}:${minute}`;
            }
        },

        // 切换收藏状态
        async toggleStar(email) {
            if (email.isToggleStar) return;

            this.$set(email, 'isToggleStar', true);

            try {
                const type = email.is_starred ? 'unstar' : 'star';
                // 确保id是数值型
                const id = email.is_starred ?
                    parseInt(email.id_correspondence) || 0 :
                    parseInt(email.id) || 0;

                await changeEmailStar(type, id);
                email.is_starred = !email.is_starred;

                this.$message.success(email.is_starred ? '已收藏' : '已取消收藏');
            } catch (error) {
                console.error('切换收藏状态失败:', error);
                this.$message.error('操作失败');
            } finally {
                this.$set(email, 'isToggleStar', false);
            }
        },

        // 删除邮件
        deleteEmail(email) {
            this.$confirm('确定要删除这封邮件吗？', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                // 从列表中移除
                const index = this.emailList.indexOf(email);
                if (index > -1) {
                    this.emailList.splice(index, 1);
                }
                this.$message.success('删除成功');
            }).catch(() => {
                // 取消删除
            });
        },

        // 检查是否有附件
        hasAttachments(email) {
            return email.attachments && (
                (email.attachments.images && email.attachments.images.length > 0) ||
                (email.attachments.videos && email.attachments.videos.length > 0)
            );
        },


        // 处理视频点击
        handleVideoClick(video) {
            // 触发视频预览事件
            this.$emit('attachment-click', {
                email: null,
                attachment: video,
                type: 'video',
                index: 0,
                url: video.url_original,
                isPaid: video.is_paid
            });
        },

        // 处理附件点击
        handleAttachmentClick(email, attachment, type, index) {
            // 由于使用了预览组件，这里主要用于记录点击事件
            // 预览功能由组件内部处理
            // this.$emit('attachment-click', {
            //     email,
            //     attachment,
            //     type,
            //     index,
            //     url: attachment.url_original || attachment.url_thumbnail,
            //     isPaid: attachment.is_paid
            // });
        },

        // 获取附件URL
        getAttachmentUrl(email, attachment, key) {
            // 这里需要根据实际的附件URL生成逻辑来实现
            // 暂时返回一个占位符
            return `#attachment-${email.id}-${key}`;
        },

        // 判断是否为图片附件
        isImageAttachment(attachment) {
            return attachment && attachment.mediatype && attachment.mediatype.indexOf('image') !== -1;
        },

        // 判断是否为音频附件
        isAudioAttachment(attachment) {
            return attachment && attachment.mediatype && attachment.mediatype.indexOf('audio') !== -1;
        },

        // 判断是否为视频附件
        isVideoAttachment(attachment) {
            return attachment && attachment.mediatype && attachment.mediatype.indexOf('video') !== -1;
        },

        // 格式化文件大小
        formatFileSize(attachment) {
            if (!attachment || !attachment.size) return '';

            const size = attachment.size;
            if (size < 1024) {
                return size + ' B';
            } else if (size < 1024 * 1024) {
                return (size / 1024).toFixed(1) + ' KB';
            } else {
                return (size / (1024 * 1024)).toFixed(1) + ' MB';
            }
        },

        // 返回上一页
        goBack() {
            this.$emit('go-back');
        },

        // 回复邮件
        replyEmail() {
            this.showReplyModal = true;
        },

        // 刷新邮件列表
        refresh() {
            this.loadEmails();
        },

        // 测试方法：验证标题显示
        testTitleDisplay() {
            console.log('邮件列表:', this.emailList);
            this.emailList.forEach(email => {
                console.log(`邮件 ${email.id} 标题:`, email.title || '无标题');
            });
        },

        // 测试方法：验证发送者判断
        testSenderLogic() {
            console.log('correspondenceInfo:', this.correspondenceInfo);
            console.log('userInfo:', this.userInfo);
            this.emailList.forEach(email => {
                const senderName = this.getSenderName(email);
                const senderAvatar = this.getSenderAvatar(email);
                console.log(`邮件 ${email.id}:`, {
                    id_user_from: email.id_user_from,
                    id_user_to: email.id_user_to,
                    senderName,
                    senderAvatar,
                    isCurrentUser: email.id_user_from === this.correspondenceInfo.idTrustedUser
                });
            });
        },

        // 去除HTML标签
        stripHtmlTags(html) {
            if (!html) return '';
            return html.replace(/<[^>]*>/g, '').trim();
        }
    }
}
</script>

<style scoped lang="scss">
.email-detail-list {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 8px;
    overflow: hidden;

    .email-detail-toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        border-bottom: 1px solid #e9ecef;
        background: #fff;

        .toolbar-left {
            display: flex;
            align-items: center;
            gap: 16px;

            .user-info {
                display: flex;
                align-items: center;
                gap: 12px;

                .user-avatar {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    object-fit: cover;
                }

                .user-details {
                    .user-name {
                        font-size: 16px;
                        font-weight: 600;
                        color: #212529;
                        margin-bottom: 4px;
                    }

                    .user-status {
                        font-size: 12px;
                        padding: 2px 8px;
                        border-radius: 12px;
                        width: fit-content;

                        &.online {
                            background: #d4edda;
                            color: #155724;
                        }

                        &.offline {
                            background: #f8d7da;
                            color: #721c24;
                        }
                    }
                }
            }
        }
    }

    .email-list-container {
        flex: 1;
        overflow-y: auto;
        padding: 20px;

        .loading-container,
        .empty-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 200px;
            color: #909399;

            .empty-icon {
                margin-bottom: 16px;

                img {
                    width: 56px;
                    height: 56px;
                    opacity: 0.8;
                }
            }

            .empty-title {
                font-size: 16px;
                font-weight: 500;
                color: #606266;
                margin-bottom: 8px;
            }

            .empty-subtitle {
                font-size: 12px;
                color: #909399;
            }
        }

        .email-list {
            .email-item {
                margin-bottom: 12px;
                border: 1px solid #e9ecef;
                border-radius: 8px;
                background: #fff;
                transition: all 0.3s ease;
                overflow: hidden;

                &:hover {
                    border-color: #409eff;
                    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
                }

                &.expanded {
                    border-color: #409eff;
                    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
                }

                .email-header {
                    display: flex;
                    align-items: center;
                    padding: 16px 20px;
                    cursor: pointer;
                    transition: background-color 0.2s ease;
                    gap: 16px;

                    &:hover {
                        background-color: #f8f9fa;
                    }

                    .email-sender-info {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        min-width: 120px;

                        .sender-avatar-container {
                            position: relative;

                            .sender-avatar {
                                width: 40px;
                                height: 40px;
                                border-radius: 50%;
                                object-fit: cover;
                            }

                            .online-indicator {
                                position: absolute;
                                bottom: 0;
                                right: 0;
                                width: 12px;
                                height: 12px;
                                border-radius: 50%;
                                border: 2px solid #fff;

                                &.online {
                                    background-color: #28a745;
                                }

                                &.offline {
                                    background-color: #6c757d;
                                }
                            }
                        }

                        .sender-name {
                            font-size: 14px;
                            font-weight: 600;
                            color: #212529;
                        }
                    }

                    .email-preview {
                        flex: 1;
                        min-width: 0;

                        .email-content-preview {
                            font-size: 14px;
                            color: #6c757d;
                            line-height: 1.4;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;

                            &.unread {
                                color: #212529;
                                font-weight: 500;
                            }
                        }
                    }

                    .email-meta-actions {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        flex-shrink: 0;

                        .attachment-indicator {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            width: 16px;
                            height: 16px;

                            img {
                                width: 16px;
                                height: 16px;
                                opacity: 0.6;
                            }
                        }

                        .status-tag {
                            display: flex;
                            align-items: center;
                            gap: 4px;
                            font-size: 12px;
                            padding: 4px 8px;
                            border-radius: 12px;
                            white-space: nowrap;

                            img {
                                width: 12px;
                                height: 12px;
                            }

                            &.read {
                                background: #d4edda;
                                color: #155724;
                            }

                            &.unread {
                                background: #f8d7da;
                                color: #721c24;
                            }
                        }

                        .email-time {
                            font-size: 12px;
                            color: #6c757d;
                            white-space: nowrap;
                        }

                        .star-icon {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            width: 20px;
                            height: 20px;
                            cursor: pointer;
                            transition: opacity 0.2s ease;

                            &:hover {
                                opacity: 0.7;
                            }

                            img {
                                width: 16px;
                                height: 16px;
                            }
                        }
                    }
                }

                .email-content {
                    padding: 0 20px 20px 20px;

                    .email-body {
                        font-size: 14px;
                        color: #6c757d;
                        line-height: 1.6;
                        margin-bottom: 16px;

                        &.unread {
                            color: #495057;
                        }

                        // 处理HTML内容样式
                        ::v-deep {
                            p {
                                margin-bottom: 8px;
                            }

                            img {
                                max-width: 100%;
                                height: auto;
                                border-radius: 4px;
                            }
                        }
                    }

                    .email-attachments {
                        border-top: 1px solid #e9ecef;
                        padding-top: 16px;

                        .attachment-header {
                            display: flex;
                            align-items: center;
                            gap: 8px;
                            margin-bottom: 16px;
                            font-size: 14px;
                            font-weight: 600;
                            color: #495057;

                            img {
                                width: 16px;
                                height: 16px;
                            }
                        }

                        .attachment-list {
                            display: flex;
                            flex-direction: column;
                            gap: 16px;

                            .attachment-section {
                                .section-title {
                                    font-size: 12px;
                                    font-weight: 600;
                                    color: #6c757d;
                                    margin-bottom: 8px;
                                    text-transform: uppercase;
                                }

                                .attachment-items {
                                    display: flex;
                                    flex-wrap: wrap;
                                    gap: 12px;

                                    .attachment-item {
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        cursor: pointer;
                                        width: 80px;
                                        height: 80px;

                                        // &:hover {
                                        //     .post-img,
                                        //     .post-video {
                                        //         transform: scale(1.05);
                                        //     }
                                        // }

                                        .attachment-preview-container {
                                            position: relative;
                                            width: 80px;
                                            height: 80px;
                                            overflow: hidden;
                                        }

                                        .attachment-preview {
                                            width: 100%;
                                            height: 100%;
                                            border-radius: 6px;
                                            object-fit: cover;
                                        }

                                        .post-img {
                                            width: 100%;
                                            height: 100%;
                                            border-radius: 6px;
                                            object-fit: cover;
                                            cursor: pointer;
                                        }

                                        // 确保没有意外的数字显示
                                        .attachment-preview-container {

                                            &::before,
                                            &::after {
                                                display: none !important;
                                            }

                                            // 隐藏任何可能的数字显示
                                            .preview-image {

                                                &::before,
                                                &::after {
                                                    display: none !important;
                                                }
                                            }
                                        }



                                        .seen-overlay {
                                            position: absolute;
                                            top: 4px;
                                            right: 4px;
                                            z-index: 2;
                                            pointer-events: none;
                                        }

                                        .seen-tag {
                                            display: inline-flex;
                                            align-items: center;
                                            gap: 3px;
                                            background: rgba(212, 237, 218, 0.9);
                                            color: #155724;
                                            font-size: 9px;
                                            font-weight: 500;
                                            padding: 2px 6px;
                                            border-radius: 8px;
                                            backdrop-filter: blur(2px);
                                            pointer-events: none;

                                            img {
                                                width: 8px;
                                                height: 8px;
                                            }
                                        }

                                        .seen-red {
                                            background: rgba(255, 243, 205, 0.9);
                                            color: #856404;
                                        }

                                        .video-preview {
                                            position: relative;
                                            width: 100%;
                                            height: 100%;

                                            .attachment-preview {
                                                width: 100%;
                                                height: 100%;
                                                border-radius: 6px;
                                                object-fit: cover;
                                            }

                                            .video-play-icon {
                                                position: absolute;
                                                top: 50%;
                                                left: 50%;
                                                transform: translate(-50%, -50%);
                                                width: 24px;
                                                height: 24px;
                                                background: rgba(0, 0, 0, 0.6);
                                                border-radius: 50%;
                                                display: flex;
                                                align-items: center;
                                                justify-content: center;
                                                z-index: 1;
                                                pointer-events: none;

                                                img {
                                                    width: 12px;
                                                    height: 12px;
                                                    filter: brightness(0) invert(1);
                                                }
                                            }
                                        }


                                    }
                                }
                            }
                        }
                    }
                }

                .email-actions {
                    display: flex;
                    justify-content: flex-end;
                    margin: 0 20px 20px 20px;
                    padding-top: 16px;
                    border-top: 1px solid #e9ecef;

                    .action-buttons {
                        display: flex;
                        gap: 8px;

                        .action-icon {
                            width: 16px;
                            height: 16px;
                        }
                    }
                }
            }

            .load-more,
            .no-more {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 20px;
                color: #909399;
            }
        }
    }
}

// 滚动条样式
.email-list-container {
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 3px;

        &:hover {
            background: #a8a8a8;
        }
    }
}
</style>